// Mi marca › «Cuaderno de trabajo» — browser helpers: the identity's font files (loaded once, measured with fontkit
// and registered for the SVG preview, so the preview and the PDF break lines identically), the photos the layout
// needs, and the PDF download. pdf-lib and fontkit load only here, on demand: the rest of Oike does not carry them.
import { IDENTITY_A, identityAssetUrl } from '../../../brandIdentity.js';
import { makeMeasure, buildNotebookPdf, FONT_FEATURES } from '../../../notebookPdf.js';
import { loadPhoto } from './brandClient.js';

let fontsPromise = null;
/** { bytes, measure } of identity A. Rejects with a clear message when the files cannot be read. */
export function notebookFonts() {
  if (!fontsPromise) {
    fontsPromise = (async () => {
      const fontkit = (await import('@pdf-lib/fontkit')).default;
      const entries = await Promise.all(Object.entries(IDENTITY_A.fonts).map(async ([key, f]) => {
        const res = await fetch(identityAssetUrl(`${IDENTITY_A.fontDir}/${f.file}`));
        if (!res.ok) throw new Error('font');
        const bytes = new Uint8Array(await res.arrayBuffer());
        try {
          const face = new FontFace(f.family, bytes.slice().buffer, { weight: String(f.weight), style: 'normal' });
          await face.load(); document.fonts.add(face);
        } catch { /* the PDF still has the font; the preview would fall back */ }
        return [key, bytes];
      }));
      const bytes = Object.fromEntries(entries);
      const faces = Object.fromEntries(entries.map(([k, b]) => [k, fontkit.create(b)]));
      return { bytes, fontkit, faces, measure: makeMeasure(faces) };
    })().catch((e) => { fontsPromise = null; throw new Error('No se pudieron cargar las tipografías del cuaderno. Revisá la conexión y volvé a intentarlo.', { cause: e }); });
  }
  return fontsPromise;
}

/** The photos of a notebook, loaded through the server (size for the layout, data for the preview and the PDF). */
export async function notebookImages(ids) {
  const out = {};
  for (const id of [...new Set(ids)]) {
    try { const p = await loadPhoto(id); out[id] = { w: p.w, h: p.h, src: p.src, img: p.img }; } catch { /* reported by the layout as loading */ }
  }
  return out;
}

const dataBytes = (dataUrl) => { const b = atob(String(dataUrl).split(',')[1] || ''); const u = new Uint8Array(b.length); for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i); return u; };
/** PNG or JPEG bytes of a loaded photo (another type is converted to PNG, keeping transparency). */
async function photoBytes(im) {
  if (/^data:image\/png/.test(im.src)) return { bytes: dataBytes(im.src), type: 'png' };
  if (/^data:image\/jpe?g/.test(im.src)) return { bytes: dataBytes(im.src), type: 'jpg' };
  const c = document.createElement('canvas'); c.width = im.w; c.height = im.h;
  c.getContext('2d').drawImage(im.img, 0, 0);
  return { bytes: dataBytes(c.toDataURL('image/png')), type: 'png' };
}

/** The PDF bytes of a laid-out notebook (nothing is downloaded or recorded here). */
export async function notebookPdfBytes(layout, { images, title, author, subject = 'Cuaderno de trabajo' }) {
  const [{ bytes, fontkit }, PDFLib] = await Promise.all([notebookFonts(), import('pdf-lib')]);
  const tex = await fetch(identityAssetUrl(IDENTITY_A.texture)).then(r => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
  const used = [...new Set(layout.pages.flatMap(p => p.items.filter(i => i.t === 'image').map(i => i.photoId)))];
  const imgs = {};
  for (const id of used) if (images[id]) imgs[id] = await photoBytes(images[id]);
  return buildNotebookPdf(layout, { PDFLib, fontkit, fonts: bytes, texture: tex ? new Uint8Array(tex) : null, images: imgs, title, author, subject });
}

let texturePromise = null;
const textureImage = () => {
  if (!texturePromise) texturePromise = new Promise((resolve) => { const img = new Image(); img.onload = () => resolve(img); img.onerror = () => resolve(null); img.src = identityAssetUrl(IDENTITY_A.texture); });
  return texturePromise;
};

/**
 * One page of a layout as PNG bytes, at the exact size: the same items as the preview, and the text drawn as the
 * outlines of the same font files (with the same features off), so line breaks and letters match the preview.
 */
export async function pagePngBlob(page, size, { images = {} } = {}) {
  const [{ faces }, tex] = await Promise.all([notebookFonts(), textureImage()]);
  const canvas = document.createElement('canvas');
  canvas.width = size.w; canvas.height = size.h;
  const ctx = canvas.getContext('2d');
  for (const it of page.items) {
    if (it.t === 'rect') { ctx.fillStyle = it.fill; ctx.fillRect(it.x, it.y, it.w, it.h); }
    else if (it.t === 'texture') { if (tex) ctx.drawImage(tex, it.x ?? 0, it.y ?? 0, it.w ?? size.w, it.h ?? size.h); }
    else if (it.t === 'path') {
      const path = new Path2D(it.d);
      if (it.fill) { ctx.globalAlpha = it.opacity ?? 1; ctx.fillStyle = it.fill; ctx.fill(path); ctx.globalAlpha = 1; }
      if (it.stroke) { ctx.globalAlpha = it.opacity ?? 1; ctx.strokeStyle = it.stroke; ctx.lineWidth = it.width; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke(path); ctx.globalAlpha = 1; }
    } else if (it.t === 'text') {
      const face = faces[it.font];
      const s = it.size / face.unitsPerEm;
      let pen = it.x;
      ctx.fillStyle = it.color;
      for (const g of face.layout(String(it.text), FONT_FEATURES[it.font]).glyphs) {
        ctx.save(); ctx.translate(pen, it.y); ctx.scale(s, -s);
        ctx.fill(new Path2D(g.path.toSVG()));
        ctx.restore();
        pen += g.advanceWidth * s;
      }
    } else if (it.t === 'image') {
      const im = images[it.photoId];
      if (!im?.img) continue;
      ctx.save(); ctx.beginPath(); ctx.rect(it.clip.x, it.clip.y, it.clip.w, it.clip.h); ctx.clip();
      ctx.drawImage(im.img, it.x, it.y, it.w, it.h);
      ctx.restore();
    }
  }
  const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('No se pudo crear el PNG.');
  return blob;
}

export function downloadBlob(blob, name) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return { bytes: blob.size, name };
}

export const pdfFileName = (title) => `${String(title || 'cuaderno').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase().slice(0, 60) || 'cuaderno'}.pdf`;

export function downloadBytes(bytes, name) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return { bytes: blob.size, name };
}
